import React, { useEffect, useLayoutEffect, useState } from 'react'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Icon,
  Popover,
  Select,
  Textfield
} from '@clientbase/clientbase-library'
import { MenuItem, SelectChangeEvent, useMediaQuery } from '@mui/material'
import { ParsedUrlQuery } from 'querystring'
import { OVERDUE_STATUS } from 'templates/v2/Charge/components/ChargeFilters/ChargeFilters.helpers'

import { Order, OrderParams, OrderState } from 'components/Table'

import { handleDeleteQueryFilters } from 'utils/query-string'

import * as S from './ModalPaymentLinkReport.styles'

export type FiltersCobrancaParams = {
  customer_name?: string
  customer_group_uuid?: string
  amount_billed?: string
  status?: string[]
  source?: string
}

const statusFilters = [
  { label: 'Pendente', value: 'pending' },
  { label: 'Aberta', value: 'open_payment' },
  { label: 'Em atraso', value: OVERDUE_STATUS },
  { label: 'Pago', value: 'paid' },
  { label: 'Expirada', value: 'no_payment;expiring' },
  { label: 'Cancelada', value: 'cancelled' },
  { label: 'Cancelando', value: 'cancelling' }
]

type ReportChargeFiltersProps = {
  baseFilters: { [key in string]: string } | ParsedUrlQuery
  enableFilterByName?: boolean
  setBaseFilters: (params: {
    [key in string]: string | number | object
  }) => void
}

const ReportChargeFilters = ({
  baseFilters,
  setBaseFilters,
  enableFilterByName
}: ReportChargeFiltersProps) => {
  const [isOpenStatus, setIsOpenStatus] = useState(false)

  const [sortCharge, setSortCharge] = useState<OrderState>({
    label: 'due_date',
    sort: 'desc'
  })
  const customer_name = baseFilters?.customer_name
  const amount_billed = baseFilters?.amount_billed
  const status = baseFilters?.status

  const [filters, setFilters] = useState<FiltersCobrancaParams>({})
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const filtersTextfields = [
    {
      name: 'customer_name',
      label: 'Nome'
    },
    {
      name: 'amount_billed',
      label: 'Valor:'
    }
  ]

  const filterByNameField = {
    name: 'customer_name',
    label: 'Nome:'
  }

  enableFilterByName && filtersTextfields.unshift(filterByNameField)

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleOnChangeOrder = (value: string, order: OrderParams) => {
    const newValue = { label: value, sort: order }
    setSortCharge(newValue)

    if (value === 'initial') {
      // eslint-disable-next-line
      const { sort, ...filtersWithoutSort } = baseFilters
      setBaseFilters({ query: filtersWithoutSort })
      return
    }

    setBaseFilters({ query: { ...baseFilters, sort: `${value}:${order}` } })
  }

  const handleClearMenagementType = () => {
    // eslint-disable-next-line
    const { scheduled, issued, ...rest } = baseFilters
    return rest
  }

  const handleDeleteFilter = (item: string) => {
    const currencyFilter = handleDeleteQueryFilters({
      filters: baseFilters,
      options: [item]
    })
    setBaseFilters({ query: currencyFilter })
    setAnchorEl(null)
  }

  const handleChangeTextField = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target
    setFilters({ ...filters, [name]: value })
  }

  const handleChangeSelect = (
    event: SelectChangeEvent<unknown>,
    key: string
  ) => {
    const value = event.target.value as string
    setFilters({ ...filters, [key]: value })
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleFilter = () => {
    const newFilters = handleClearMenagementType()
    const { status, ...filter } = { ...newFilters, ...filters }
    if (status && status.length > 0) {
      setBaseFilters({
        query: {
          ...filter,
          status: Array.isArray(status) ? status.join('|') : status,
          page: 0
        }
      })
    } else {
      setBaseFilters({
        query: {
          ...filter,
          page: 0
        }
      })
    }
    handleClose()
  }
  useEffect(() => {
    handleClearFilters()
  }, [])

  const handleClearFilters = () => {
    const currencyFilter = handleDeleteQueryFilters({
      filters: baseFilters,
      options: ['customer_name', 'amount_billed', 'status']
    })

    setBaseFilters({ query: currencyFilter })
    handleClose()
  }

  useLayoutEffect(() => {
    const statusValue = Array.isArray(status) ? status : status?.split('|')
    const statusMapped = statusValue?.map((item) =>
      item === 'no_payment' ? 'no_payment;expiring' : item
    )

    if (statusMapped && statusMapped?.indexOf('expiring') >= 0) {
      delete statusMapped[statusMapped?.indexOf('expiring')]
    }

    setFilters({
      ...(!!customer_name && { customer_name }),
      ...(!!amount_billed && { amount_billed }),
      ...(!!status && { status: statusMapped })
    } as FiltersCobrancaParams)
  }, [baseFilters])

  useLayoutEffect(() => {
    if (baseFilters.sort) {
      const [value, order] = (baseFilters.sort as string).split(':')
      const newValue = {
        label: value || 'initial',
        sort: (order || 'desc') as OrderParams
      }
      setSortCharge(newValue)
    }
  }, [baseFilters])

  const isMobile = useMediaQuery('(max-width: 600px)', { noSsr: false })

  return (
    <div>
      <div>
        <S.ButtonsWrapper>
          <Order
            handleOnChange={handleOnChangeOrder}
            options={[
              { label: 'Organizar por:', value: 'initial' },
              { label: 'Por vencimento', value: 'due_date' },
              { label: 'Por valor cobrado', value: 'amount_billed' },
              { label: 'Por status', value: 'status' },
              { label: 'Por data de pagamento', value: 'date_paid' },
              { label: 'Por valor pago', value: 'amount_paid' }
            ]}
            value={sortCharge}
          />

          <Button
            aria-controls={open ? 'button-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            id="button-filters"
            onClick={handleClick}
            variantButton="neutral"
            fullWidth={isMobile}
          >
            Filtros: {Object.keys(filters).length}
          </Button>
        </S.ButtonsWrapper>

        <Popover
          anchorEl={anchorEl}
          id="menu-filters"
          onClose={handleClose}
          open={open}
        >
          <S.PopoverContainer>
            <S.ScrollableContent>
              {filtersTextfields.map(({ name, label }) => (
                <React.Fragment key={name}>
                  <Accordion>
                    <AccordionSummary>
                      <S.AccordionSummaryContent>
                        &#8288;{label}
                        {!!filters[name] && (
                          <Icon
                            icon="cancel"
                            onClick={() => handleDeleteFilter(name)}
                            width="20px"
                            sx={{ mr: 1 }}
                          />
                        )}
                      </S.AccordionSummaryContent>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Textfield
                        name={name}
                        onChange={handleChangeTextField}
                        value={filters[name]}
                        {...(name === 'amount_billed' && {})}
                      />
                    </AccordionDetails>
                  </Accordion>
                </React.Fragment>
              ))}

              <Accordion>
                <AccordionSummary>
                  <S.AccordionSummaryContent>
                    Status:
                    {!!filters?.status?.length && (
                      <Icon
                        icon="cancel"
                        onClick={() => handleDeleteFilter('status')}
                        width="20px"
                        sx={{ mr: 1 }}
                      />
                    )}
                  </S.AccordionSummaryContent>
                </AccordionSummary>
                <AccordionDetails>
                  <Select
                    fullWidth={true}
                    name="status"
                    data-cy="status"
                    onChange={(e) => handleChangeSelect(e, 'status')}
                    value={filters?.status || []}
                    open={isOpenStatus}
                    onClick={() => setIsOpenStatus(!isOpenStatus)}
                    multiple
                  >
                    {statusFilters.map(({ label, value }, index) => (
                      <MenuItem key={index} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </AccordionDetails>
              </Accordion>
            </S.ScrollableContent>

            <S.FixedButtonsContainer>
              <Button
                onClick={handleClearFilters}
                fullWidth
                variantButton="neutral"
              >
                Limpar
              </Button>
              <Button onClick={handleFilter} fullWidth>
                Filtrar
              </Button>
            </S.FixedButtonsContainer>
          </S.PopoverContainer>
        </Popover>
      </div>
    </div>
  )
}

export default ReportChargeFilters
